import { useMemo } from 'react'

import { AgChartOptions } from 'ag-charts-community'

import { hexLighten } from '@fortem-monorepo/js-utilities'
import { CustomAgChart } from '@fortem-monorepo/react-ag'

import { ICReportChartPrimitive } from '@/pages/funds/ic-report/components/primitives/types'
import { commodities } from '@/react-query/queries/ic-report/hooks'

const groups = [
  {
    order: 1,
    color: '#c05015', // orange
    items: ['WTI Crude Oil', 'Heating Oil', 'Nat Gas', 'RBOB'],
  },
  {
    order: 2,
    color: '#ffc000', // gold
    items: ['Gold', 'Silver'],
  },
  {
    order: 3,
    color: '#595959', // grey
    items: ['Copper', 'Aluminium', 'Zinc', 'Nickel', 'Lead'],
  },
  {
    order: 4,
    color: '#0b76a0', // lightblue
    items: ['Corn', 'Wheat', 'Kansas Wheat', 'Soybean', 'Soybean Oil', 'Soybean Meal'],
  },
  {
    order: 5,
    color: '#156082', // darkblue
    items: ['Sugar', 'Coffee', 'Cotton'],
  },
  {
    order: 6,
    color: '#80360e', // red
    items: ['Live Cattle', 'Lean Hogs', 'BCOM'],
  },
]

export interface CommodityBackwardationChartProps extends ICReportChartPrimitive {}

export const CommodityBackwardationChart: React.FC<CommodityBackwardationChartProps> = ({
  params,
  options,
}) => {
  const query = commodities.useBackwardation(params)
  const data = query.data ?? []

  const dataFormatted = useMemo(() => {
    return data
      .map((item) => {
        return {
          ...item,
          short_name: item.short_name.split(' ').slice(1).join(' '),
        }
      })
      .sort((a: any, b: any) => {
        const itemA = groups.find((group) => group.items.includes(a.short_name))
        const itemB = groups.find((group) => group.items.includes(b.short_name))

        // @ts-ignore
        return itemA?.order - itemB?.order
      })
  }, [data])

  const mergedOptions: AgChartOptions = {
    height: 500,
    series: [
      { key: 'backwardation_3_months', name: '3m Backwardation' },
      { key: 'backwardation_6_months', name: '6m Backwardation' },
    ].map((item, index) => {
      return {
        data: dataFormatted,
        type: 'bar',
        xKey: 'short_name',
        yKey: item.key,
        yName: `${item.name} (${index > 0 ? 'right' : 'left'})`,
        itemStyler: (params) => {
          const colourStr = groups.filter(
            (group) => group.items.filter((items) => items.includes(params.xValue)).length > 0,
          )?.[0].color

          return {
            fill: hexLighten(colourStr, index > 0 ? 20 : -20),
          }
        },

        styler: (params) => {
          return {
            // fill: colourStr,
            fill: params.stroke,
          }
        },
      }
    }),
    axes: [
      {
        type: 'category',
        position: 'bottom',
      },
      {
        type: 'number',
        position: 'left',
        label: {
          formatter: (params) => {
            return params.index >= 0
              ? `${params?.value * 100}%`
              : `${(params?.value * 100).toFixed(2)}%`
          },
        },
      },
    ],
    ...options,
  }

  return <CustomAgChart options={mergedOptions} />
}
